import { zodResolver } from '@hookform/resolvers/zod';
import { useDeleteETSTicketNotificationMutation } from 'gcs-common/clients/api/entities/tickets/tickets-api';
import { useGetETSTicketMessagesInformationSelector } from 'gcs-common/clients/api/entities/tickets/tickets-api-selectors';
import TicketMessage from 'gcs-common/components/Tickets/TicketMessage/TicketMessage';
import { Form } from 'gcs-common/design-system/ui/form';
import LoadingSpinner from 'gcs-common/design-system/ui/loading-spinner';
import { useTicketDropzone } from 'gcs-common/hooks/useTicketDropzone';
import { getCurrentUserId } from 'gcs-common/slices/currentUser/currentUserSelectors';
import { useEffect, useMemo, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import { z } from 'zod';
import TicketCommentForm from '../TicketCommentForm/TicketCommentForm';
import TicketDragArea from '../TicketDragArea/TicketDragArea';
import TicketCreatedByColleaguesAlert from './ticket-created-by-colleagues-alert';

const formatDate = (isoString: string) => new Date(isoString).toISOString().split('T')[0];

const formatDateDisplay = (isoString: string) => {
  if (isoString.length === 0) {
    return;
  }
  const date = new Date(isoString);
  const today = new Date();

  return date.toISOString().split('T')[0] === today.toISOString().split('T')[0]
    ? 'Heute'
    : `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
};

const TicketFormSchema = z.object({
  comment: z.string().trim().min(1),
  attachments: z.array(z.instanceof(File)).optional(),
});

export type TicketCommentFormValues = z.infer<typeof TicketFormSchema>;

const TicketDetails = () => {
  const { routeTicketId } = useParams();
  const ticketId = routeTicketId ?? '';
  const currentUserId: string = useSelector(getCurrentUserId);
  const deleteETSTicketNotification = useDeleteETSTicketNotificationMutation();
  const { messages, isPending, createdByUserId, creatorName } = useGetETSTicketMessagesInformationSelector({
    ticketId,
    currentUserId,
  });
  const virtuosoRef = useRef<VirtuosoHandle>(null);

  const createdByColleague = currentUserId !== createdByUserId;

  const form = useForm<z.infer<typeof TicketFormSchema>>({
    resolver: zodResolver(TicketFormSchema),
    defaultValues: {
      comment: '',
      attachments: [],
    },
    mode: 'onChange',
  });
  const attachments = useWatch({ control: form.control, name: 'attachments' });
  const hasAttachments = attachments && attachments.length > 0;

  useEffect(() => {
    setTimeout(() => {
      virtuosoRef.current?.scrollToIndex({
        index: messages?.length - 1,
        align: 'start',
        behavior: 'auto',
      });
    }, 100);
  }, [messages]);

  // clear notifications when component unmounts.
  useEffect(() => {
    return () => {
      deleteETSTicketNotification.mutate({ path: { id: ticketId } });
    };
  }, [ticketId]);

  const groupedMessages = useMemo(() => {
    const groups: Record<string, typeof messages> = {};
    const messagesWithoutDate: typeof messages = [];

    for (const message of messages) {
      if (!('createdAt' in message) || !message.createdAt) {
        // Collect messages without a createdAt date separately
        messagesWithoutDate.push(message);
        continue;
      }

      const dateKey = formatDate(message.createdAt) || '';
      if (dateKey) {
        if (!groups[dateKey]) {
          groups[dateKey] = [];
        }
        groups[dateKey].push(message);
      }
    }

    // Sort groups by date
    const sortedGroups = Object.entries(groups).sort(([a], [b]) => (a > b ? 1 : -1));

    // Append messages without date at the end
    if (messagesWithoutDate.length > 0) {
      sortedGroups.push(['', messagesWithoutDate]);
    }

    return sortedGroups;
  }, [messages]);

  const { getRootProps, getInputProps, isDragActive } = useTicketDropzone({
    form,
    attachmentsField: 'attachments',
  });

  if (isPending) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div {...getRootProps()} className="flex flex-grow flex-col justify-between">
      <input {...getInputProps()} />

      {isDragActive ? (
        <TicketDragArea />
      ) : (
        <>
          {/* Hide messages if attachments exist */}
          {!hasAttachments && (
            <>
              {createdByColleague && TicketCreatedByColleaguesAlert({ creatorName })}
              <div className="mb-2 flex-grow pb-6">
                <Virtuoso
                  ref={virtuosoRef}
                  followOutput={true}
                  data={groupedMessages}
                  className="overflow-x-hidden p-4"
                  initialTopMostItemIndex={messages.length - 1}
                  itemContent={(_, [date, messagesForDate]) => (
                    <div key={date}>
                      <p className="mb-2 text-center text-neutral-9 text-sm">{formatDateDisplay(date)}</p>
                      {messagesForDate.map((message, key) => (
                        <TicketMessage
                          key={`messages-${ticketId}-${message.type}-${key}`}
                          message={message}
                          ticketId={ticketId}
                        />
                      ))}
                    </div>
                  )}
                />
              </div>
            </>
          )}
          {!createdByColleague && (
            <div className={`w-full bg-neutral-1 ${hasAttachments ? 'h-full' : ''}`}>
              <Form {...form}>
                <TicketCommentForm />
              </Form>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TicketDetails;
